/**
 * QAIChatPanel - Inline Q.AI Chat for Dashboard Integration
 *
 * Embeddable chat panel that can be toggled open/closed in dashboards.
 * Uses existing QChatWindow and useQChat hook.
 *
 * Features:
 * - Minimize/Maximize/Close controls
 * - Compact header
 * - Resizable width ('half' | 'full')
 * - Smooth transitions
 */

import React, { memo, useCallback } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import MinimizeIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import QChatWindow from '../QAI/QChatWindow';
import useQChat from '../../hooks/useQChat';

/**
 * QAIChatPanel Component
 *
 * Props:
 * - isOpen, isMinimized, isMaximized: Window state controls
 * - onClose, onMinimize, onMaximize: State change handlers
 * - token: JWT authentication token
 * - aiContext: (Optional) Pre-computed AI context from QuantX API (Task 1.4)
 * - symbol: (Optional) Current trading symbol for context
 */
const QAIChatPanel = memo(({
    isOpen = false,
    isMinimized = false,
    isMaximized = false,
    onClose,
    onMinimize,
    onMaximize,
    token,
    aiContext = null,  // Task 1.4: Pre-computed market context from /api/quantx/ai-context
    symbol = null      // Current symbol for context-aware prompts
}) => {
    // Initialize Q.AI chat hook
    // Note: Hook exports specific names - map them to what QChatWindow expects
    const {
        messages,
        balance,
        isLoading,
        isLowBalance,
        canSend,
        error,
        hasConversation,
        conversationId,
        conversationHistory,
        isLoadingHistory,
        sendMessage,
        clearMessages,              // Hook exports clearMessages
        cancelStream,
        loadConversation,           // Hook exports loadConversation
        deleteConversation,         // Hook exports deleteConversation
        refreshConversationHistory  // Hook exports refreshConversationHistory
    } = useQChat(token);

    // Task 1.4: Log aiContext when available for debugging
    // Future: Pass aiContext to backend with chat requests for smarter responses
    if (aiContext && symbol) {
        console.log('[QAIChatPanel] AI Context available for', symbol, ':', {
            hasContext: !!aiContext.contextText,
            suggestedQuestions: aiContext.suggestedQuestions?.length || 0
        });
    }

    // Handle message send
    const handleSendMessage = useCallback((message, type) => {
        sendMessage(message, type);
    }, [sendMessage]);

    // Handle action card clicks
    const handleActionCard = useCallback((actionId) => {
        // Handle quick action cards
        console.log('[QAIChatPanel] Action card clicked:', actionId);
        return { focusInput: true };
    }, []);

    // Handle action chip clicks
    // Task 1.4: Enhanced with aiContext suggested questions
    const handleActionChip = useCallback((chipId) => {
        // Send predefined prompts based on chip
        const chipPrompts = {
            'analyze-signals': 'Analyze current trading signals',
            'review-performance': 'Review my recent trading performance',
            'market-analysis': symbol
                ? `What is the current market outlook for ${symbol}?`
                : 'What is the current market outlook?',
            'coaching-tip': 'Give me a trading coaching tip'
        };

        // Task 1.4: Use suggested questions from aiContext if available
        if (chipId === 'ai-suggested' && aiContext?.suggestedQuestions?.length > 0) {
            const suggested = aiContext.suggestedQuestions[0];
            sendMessage(suggested, 'chat');
            return;
        }

        const prompt = chipPrompts[chipId];
        if (prompt) {
            sendMessage(prompt, 'chat');
        }
    }, [sendMessage, symbol, aiContext]);

    if (!isOpen) return null;

    // Minimized state - compact bar with expand option
    if (isMinimized) {
        return (
            <Box
                sx={{
                    height: '32px',
                    backgroundColor: '#0A0A0F',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 8px',
                    cursor: 'pointer',
                    borderBottom: '1px solid rgba(61, 234, 255, 0.2)'
                }}
                onClick={onMinimize}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography sx={{ fontSize: '11px', color: '#3deaff', fontWeight: 600 }}>
                        Q.AI
                    </Typography>
                    {messages.length > 1 && (
                        <Box
                            sx={{
                                backgroundColor: '#3deaff',
                                color: '#0A0A0F',
                                fontSize: '9px',
                                fontWeight: 700,
                                padding: '1px 5px',
                                borderRadius: '6px'
                            }}
                        >
                            {messages.length - 1}
                        </Box>
                    )}
                </Box>
                <IconButton size="small" sx={{ color: '#6b7280', padding: '2px' }}>
                    <OpenInFullIcon sx={{ fontSize: 12 }} />
                </IconButton>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                backgroundColor: '#0A0A0F',
                overflow: 'hidden',
                transition: 'all 0.3s ease'
            }}
        >
            {/* Compact Control Bar - Only window controls, no duplicate branding */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    padding: '4px 8px',
                    borderBottom: '1px solid rgba(61, 234, 255, 0.15)',
                    backgroundColor: 'rgba(61, 234, 255, 0.03)',
                    minHeight: '28px',
                    gap: 0.5
                }}
            >
                {/* Token Balance */}
                {balance !== null && (
                    <Typography
                        sx={{
                            fontSize: '9px',
                            color: balance > 15 ? '#22c55e' : '#fbbf24',
                            fontFamily: 'monospace',
                            marginRight: 'auto'
                        }}
                    >
                        {balance} tokens
                    </Typography>
                )}

                {/* Minimize */}
                <Tooltip title="Minimize" arrow placement="top">
                    <IconButton
                        onClick={onMinimize}
                        size="small"
                        sx={{
                            color: '#6b7280',
                            padding: '2px',
                            '&:hover': { color: '#3deaff', backgroundColor: 'rgba(61, 234, 255, 0.1)' }
                        }}
                    >
                        <MinimizeIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                </Tooltip>

                {/* Maximize/Restore */}
                <Tooltip title={isMaximized ? "Restore" : "Maximize"} arrow placement="top">
                    <IconButton
                        onClick={onMaximize}
                        size="small"
                        sx={{
                            color: '#6b7280',
                            padding: '2px',
                            '&:hover': { color: '#3deaff', backgroundColor: 'rgba(61, 234, 255, 0.1)' }
                        }}
                    >
                        {isMaximized ? (
                            <CloseFullscreenIcon sx={{ fontSize: 12 }} />
                        ) : (
                            <OpenInFullIcon sx={{ fontSize: 12 }} />
                        )}
                    </IconButton>
                </Tooltip>

                {/* Close */}
                <Tooltip title="Close" arrow placement="top">
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{
                            color: '#6b7280',
                            padding: '2px',
                            '&:hover': { color: '#ef4444', backgroundColor: 'rgba(239, 68, 68, 0.1)' }
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Chat Window - Full height, uses its own header */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <QChatWindow
                    messages={messages}
                    balance={balance}
                    isLoading={isLoading}
                    isLowBalance={isLowBalance}
                    canSend={canSend}
                    onSendMessage={handleSendMessage}
                    onActionCard={handleActionCard}
                    onActionChip={handleActionChip}
                    onCancelStream={cancelStream}
                    onClearChat={clearMessages}
                    error={error}
                    hasConversation={hasConversation}
                    conversationId={conversationId}
                    conversationHistory={conversationHistory}
                    isLoadingHistory={isLoadingHistory}
                    onSelectConversation={loadConversation}
                    onDeleteConversation={deleteConversation}
                    onRefreshHistory={refreshConversationHistory}
                />
            </Box>
        </Box>
    );
});

QAIChatPanel.displayName = 'QAIChatPanel';

export default QAIChatPanel;
